// Flask serves both the SPA and the API from the same origin in production
// (see backend/app.py), so requests are same-origin/relative by default.
export const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

// Number formatting. ',' as decimal and '.' as thousand seperator
export function formatNumber(num, decimals = 2, allowZero = true) {
  if (num === '' || num === undefined || num === null || isNaN(num)) return '';
  if (!allowZero && Number(num) === 0) return '';
  return Number(num).toLocaleString('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

// Cache token and refresh every 60 sec
let cachedToken = null;
let tokenGeneratedAt = 0;
const TOKEN_REFRESH_MS = 60000;

// Security key API key from URL
function getRawKey() {
  const params = new URLSearchParams(window.location.search);
  return params.get('key') || '';
}

// Generate signature with SubtleCrypto
async function generateHmacSignature(key, message) {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(key);
  const messageData = encoder.encode(message);
  
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  
  const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData);
  const hashArray = Array.from(new Uint8Array(signature));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Generate a time-based secure token
export async function generateSecureToken() {
  const rawKey = getRawKey();
  if (!rawKey) return '';
  
  const timestamp = Math.floor(Date.now() / 1000).toString();
  const signature = await generateHmacSignature(rawKey, timestamp);
  const tokenData = `${timestamp}:${signature}`;
  
  // Base64 URL-safe encode
  return btoa(tokenData).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function getSecureToken() {
  const now = Date.now();
  if (!cachedToken || (now - tokenGeneratedAt) > TOKEN_REFRESH_MS) {
    cachedToken = await generateSecureToken();
    tokenGeneratedAt = now;
  }
  return cachedToken;
}

// Append token to URL
export async function withKey(url) {
  const token = await getSecureToken();
  if (!token) return url;
  return url + (url.includes('?') ? '&' : '?') + 'key=' + encodeURIComponent(token);
}

// Fetch product hidden flags from local DB and apply to items
export async function resolveProductHidden(items) {
  const productIds = [...new Set(items.filter(i => i.product_id).map(i => i.product_id))];
  if (productIds.length === 0) return items;
  try {
    const resp = await fetch(await withKey(`${API_BASE}/api/zoho/products/by-ids`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: productIds }),
    });
    const data = await resp.json();
    const hiddenMap = {};
    for (const p of (data.products || [])) hiddenMap[p.id] = !!p.hidden;
    return items.map(item => {
      if (!item.product_id || !(item.product_id in hiddenMap)) return item;
      const isHidden = hiddenMap[item.product_id];
      return { ...item, product_hidden: isHidden, hidden: isHidden ? true : item.hidden };
    });
  } catch (e) {
    console.error('Error resolving product hidden flags:', e);
    return items;
  }
}